<template>
  <v-dialog
    :value="value"
    :persistent="isStreaming || isSaving"
    max-width="800"
    content-class="rename-role-dialog__content-class"
    @input="onDialogInput"
  >
    <v-card class="rename-role-dialog" width="800">
      <!-- Modal header -->
      <div class="rename-role-dialog__header">
        <div class="rename-role-dialog__featured-icon" aria-hidden="true">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
              fill="#7F56D9"
            />
            <path
              d="M6.5 18c.6-2.5 2.8-3.5 5.5-3.5s4.9 1 5.5 3.5"
              stroke="#7F56D9"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        </div>
        <div class="rename-role-dialog__heading">
          <h2 class="rename-role-dialog__title">Rename Role</h2>
        </div>
        <button
          type="button"
          class="rename-role-dialog__close"
          aria-label="Close dialog"
          @click="onClose"
        >
          <v-icon>mdi-close</v-icon>
        </button>
      </div>

      <!-- Body -->
      <RoleNameContextBody
        :name.sync="name"
        :context.sync="context"
        :error-message="saveError || nameValidation.error.value"
        :excluded-name="role.name"
        :is-archived="isArchived"
        :show-replace-popover.sync="showReplacePopover"
        :max-name-characters="maxNameCharacters"
        name-id="rename-role-dialog__name"
        context-id="rename-role-dialog__context"
        @validity-change="onValidityChange"
        @streaming-change="onStreamingChange"
        @clear-error="clearSaveError"
      />

      <!-- Footer -->
      <div class="rename-role-dialog__footer">
        <button
          type="button"
          class="rename-role-dialog__save"
          :disabled="!canSave"
          @click="handleSave"
        >
          Save
        </button>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import RoleNameContextBody from "@components/RoleNameContextBody/RoleNameContextBody.vue";
import { updateNameRole, editRole } from "@/api";
import { useOrgObjectNameValidation } from "@/composables/useOrgObjectNameValidation";

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  role: {
    type: Object,
    required: true,
  },
  maxNameCharacters: {
    type: Number,
    default: 120,
  },
});

const emit = defineEmits(["input", "saved"]);

const name = ref("");
const context = ref("");
const isStreaming = ref(false);
const isSaving = ref(false);
const isNameValid = ref(false);
const showReplacePopover = ref(false);
const saveError = ref("");

const originalName = computed(() => (props.role?.name || "").trim());
const originalContext = computed(() => (props.role?.context || "").trim());

const nameValidation = useOrgObjectNameValidation({
  type: "role",
  excludeId: () => props.role?.id,
});

// The role's own (unchanged) name is never sent to the availability check.
watch(name, (value) => {
  nameValidation.name.value = value.trim() === originalName.value ? "" : value;
});

const nameChanged = computed(() => name.value.trim() !== originalName.value);
const contextChanged = computed(() => context.value.trim() !== originalContext.value);

const isArchived = computed(() => {
  const role = props.role || {};
  return role.status === "archived" || role.archived === true;
});

const canSave = computed(
  () =>
    (nameChanged.value || contextChanged.value) &&
    isNameValid.value &&
    !nameValidation.checking.value &&
    !isStreaming.value &&
    !isSaving.value
);

function resolveAccountId() {
  try {
    const raw = localStorage.getItem("currentAccountID");
    if (raw == null || raw === "") return null;
    const parsed = JSON.parse(raw);
    return parsed != null ? parsed : raw;
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

function onValidityChange(value) {
  isNameValid.value = value;
}

function onStreamingChange(value) {
  isStreaming.value = value;
}

function clearSaveError() {
  if (saveError.value) saveError.value = "";
}

function onDialogInput(nextValue) {
  if (nextValue === true) {
    emit("input", true);
    return;
  }

  if (showReplacePopover.value) {
    showReplacePopover.value = false;
    return;
  }

  if (isStreaming.value || isSaving.value) return;

  saveError.value = "";
  emit("input", false);
}

function onClose() {
  if (isStreaming.value || isSaving.value) return;
  if (showReplacePopover.value) {
    showReplacePopover.value = false;
    return;
  }
  saveError.value = "";
  emit("input", false);
}

async function handleSave() {
  if (!canSave.value) return;

  const roleId = props.role?.id;
  if (!roleId) {
    saveError.value = "Unable to save: role ID is missing.";
    return;
  }

  const accountId = resolveAccountId();
  if (accountId == null) {
    saveError.value = "Select an account to save.";
    return;
  }

  isSaving.value = true;
  saveError.value = "";

  try {
    const updatedRole = { ...(props.role || {}) };

    if (nameChanged.value) {
      await updateNameRole(accountId, roleId, { name: name.value.trim() });
      updatedRole.name = name.value.trim();
    }

    if (contextChanged.value) {
      await editRole(accountId, roleId, { context: context.value.trim() });
      updatedRole.context = context.value.trim();
    }

    emit("saved", updatedRole);
    emit("input", false);
  } catch (error) {
    const apiError = error?.response?.data?.[0];
    if (nameValidation.validateOnSubmitError(error)) {
      // Inline "name in use" is rendered from nameValidation.error.
    } else if (
      typeof apiError === "string" &&
      /already exists|already in use|unique/i.test(apiError)
    ) {
      saveError.value = "This name is already in use. Enter a different name.";
    } else if (typeof apiError === "string" && apiError.trim()) {
      saveError.value = apiError;
    } else {
      saveError.value = "An error occurred while saving the role.";
    }
  } finally {
    isSaving.value = false;
  }
}

watch(
  () => props.value,
  (open) => {
    if (!open) return;

    name.value = props.role?.name || "";
    context.value = props.role?.context || "";
    saveError.value = "";
    nameValidation.error.value = "";
    showReplacePopover.value = false;
    isSaving.value = false;
  },
  { immediate: true }
);

watch(
  () => props.role,
  (next) => {
    if (props.value) {
      name.value = next?.name || "";
      context.value = next?.context || "";
    }
  }
);
</script>

<style scoped>
.rename-role-dialog {
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
}

.rename-role-dialog__header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px 24px 0 24px;
}

.rename-role-dialog__featured-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #eaecf0;
}

.rename-role-dialog__heading {
  flex: 1;
  min-width: 0;
}

.rename-role-dialog__title {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  color: #101828;
}

.rename-role-dialog__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #667085;
  cursor: pointer;
}

.rename-role-dialog__close:hover,
.rename-role-dialog__close:focus-visible {
  background: #f9fafb;
  outline: none;
}

.rename-role-dialog__footer {
  padding: 0 24px 24px 24px;
}

.rename-role-dialog__save {
  width: 100%;
  height: 44px;
  border: 1px solid #7f56d9;
  border-radius: 8px;
  background: #7f56d9;
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #ffffff;
  cursor: pointer;
}

.rename-role-dialog__save:hover:not(:disabled),
.rename-role-dialog__save:focus-visible {
  background: #6941c6;
  border-color: #6941c6;
  outline: none;
}

.rename-role-dialog__save:disabled {
  background: #e0e0e0;
  border-color: #e0e0e0;
  color: #475467;
  cursor: not-allowed;
}
</style>
